<script setup lang="ts">
import { computed, watch, ref, nextTick } from "vue";
import PromptComponent from "./PromptComponent.vue";
import RecurringAskPrompt from "./RecurringAskPrompt.vue";
import { PartialDisplayType, PromptType, PromptDecision, ModalDisplayType } from "../../enums";
import { AllComponents } from "@/builder/client/AllComponents";
import { useRenderStore } from "@/builder/stores/renderStore";
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

//refs
const prompt = ref();
const showPromptElements = ref(false);
const trayHeight = ref();
const trayMaxHeight = ref();
const showPromptContainer = ref(false);

const renderStore = useRenderStore();
const { activate, deactivate } = useFocusTrap(prompt);

const modalType = renderStore.page.pageDesign.modalDisplay;

//computed
const promptComponent = computed(() => {
    return renderStore.activePrompt;
});

const showPrompt = computed(() => {
    return !!promptComponent.value;
});

const isModalForm = computed(() => {
    return renderStore.page.partialDisplayType === PartialDisplayType.Modal;
});

const getModalAnimation = computed(() => {
    let animationClasses = '';

    if (showPromptContainer.value) {
        animationClasses = 'fade-in';
    }
    else {
        animationClasses = 'fade-out';
    }

    return animationClasses;
});

const getModalMaxHeight = computed(() => {
    let maxHeight = '';

    if (modalType == ModalDisplayType.Panel) {
        maxHeight = 'max-h-in-panel';
    }

    return maxHeight;
});

//methods
const closePrompt = () => {
    if (promptComponent.value) {
        renderStore.resolvePrompt(promptComponent.value?.id, PromptDecision.Decline);
    }

    if (!isModalForm.value) {
        document.body.classList.remove('rd-modal-open');
    }

    showPromptContainer.value = false;

    setTimeout(() => {
        showPromptElements.value = false;
        deactivate();
    }, 500);
};

const openPrompt = () => {
    showPromptElements.value = true;
    showPromptContainer.value = true;

    isModalForm.value ?
        prompt.value?.classList.add('tray-open') :
        document.body.classList.add('rd-modal-open');

    if (isModalForm.value) {
        nextTick(() => {
            modalType == ModalDisplayType.Panel ?
                trayMaxHeight.value = (prompt.value.offsetParent.offsetHeight - 30) + 'px' :
                trayMaxHeight.value = '700px';
        });
    }

    setTimeout(() => {
        activate();
    }, 275);
};

const onAfterEnter = () => {
    prompt.value?.classList.add('tray-open');

    if (modalType == ModalDisplayType.Lightbox) {
        prompt.value.offsetHeight < 690 ?
            trayHeight.value = prompt.value.offsetHeight + 'px' :
            trayHeight.value = '100%';
    }
    else {
        trayHeight.value = prompt.value.offsetHeight + 'px';
    }
};

const onLeave = () => {
    prompt.value?.classList.add('tray-close');
};

//watchers
watch(showPrompt, (val) => {
    if (val) {
        openPrompt();
    }
    else {
        closePrompt();
    }
});
</script>

<template>
    <div v-if="!isModalForm" id="rd-modal-container" class="raise-donors">
        <div class="rd-h-full rd-left-0 rd-overflow-x-hidden rd-overflow-y-auto rd-fixed rd-top-0 rd-w-full rd-modal"
            id="rd-modal" tabindex="-1" role="dialog" aria-label="Prompt Modal"
            :aria-hidden="showPromptContainer ? false : true" :class="showPromptContainer ? 'rd-block' : 'rd-hidden'"
            ref="prompt" @click.stop="closePrompt">

            <div @click.stop
                class="rd-items-center rd-flex rd-pointer-events-none rd-relative rd-w-auto rd-modal-wrapper rd-modal-scroll rd-modal-centered md:rd-modal-full rd-modal-prompt"
                :class="getModalAnimation" role="document">

                <div class="rd-bg-white !rd-h-auto rd-modal-content">

                    <PromptComponent>
                        <template #children>
                            <section class="prompt-children">
                                <template v-for="item in promptComponent?.components" :key="item.id">

                                    <component :is="AllComponents[item.type]" :component="item" />
                                </template>
                            </section>
                        </template>

                        <template #content>
                            <RecurringAskPrompt v-if="promptComponent?.promptType === PromptType.RecurringAsk"
                                class="prompt-children" :component="promptComponent" />
                        </template>
                    </PromptComponent>
                </div>
            </div>
        </div>

        <div class="rd-modal-backdrop"
            :class="[(showPromptContainer) ? 'fade-in-backdrop' : 'fade-out-backdrop', { 'rd-hidden': !showPromptElements }]">
        </div>
    </div>

    <div v-if="isModalForm && showPromptContainer" class="tray-overlay"
        :class="[showPromptContainer ? 'fade-in-backdrop-fast' : 'fade-out-backdrop-fast']"></div>

    <Transition name="tray" @after-enter="onAfterEnter" @leave="onLeave">

        <div v-if="isModalForm && showPrompt" id="tray" ref="prompt" class="tray" v-click-outside="closePrompt">
            <PromptComponent class="rd-items-center rd-flex rd-flex-col rd-h-full rd-w-full" :class="getModalMaxHeight">

                <template #children>
                    <template v-for="item in promptComponent?.components" :key="item.id">
                        <component :is="AllComponents[item.type]" :component="item" />
                    </template>
                </template>

                <template #content>
                    <RecurringAskPrompt v-if="promptComponent?.promptType === PromptType.RecurringAsk" class="rd-w-full"
                        :component="promptComponent" />
                </template>
            </PromptComponent>
        </div>
    </Transition>
</template>

<style lang="scss" scoped>
#raise-donors,
#rd-modal-container {
    .tray-overlay {
        background-color: rgb(37, 60, 71);
        height: 100%;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 999;
    }

    .tray {
        align-items: center;
        background-color: rgb(255 255 255 / 1);
        border-top-left-radius: 1.25rem;
        border-top-right-radius: 1.25rem;
        bottom: 0;
        box-shadow: 0px -5px 30px 0px rgba(0, 0, 0, 0.25);
        display: flex;
        flex-direction: column;
        height: 0;
        left: 0;
        overflow: hidden;
        position: absolute;
        right: 0;
        transition: transform 375ms cubic-bezier(0.445, 0.05, 0.55, 0.95);
        transform: translateY(0);
        z-index: 1000;
    }

    .prompt-container {
        &.max-h-in-panel {
            max-height: v-bind(trayMaxHeight);
        }
    }

    .rd-modal-scroll {
        .tray-open {
            height: v-bind(trayHeight);
            max-height: v-bind(trayMaxHeight);
        }
    }

    .modal-slide-in {
        .tray-open {
            height: v-bind(trayHeight);
            max-height: 100%;
        }
    }

    .tray-enter-active {
        transform: translateY(100%);
        transition: transform 375ms cubic-bezier(0.445, 0.05, 0.55, 0.95);
    }

    .tray-leave-active {
        transition: transform 250ms cubic-bezier(0.445, 0.05, 0.55, 0.95);
        transform: translateY(100%);
    }
}
</style>
